import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, CreditCard, BadgeCheck, Clock, CircleDashed, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  listAccounts,
  setActive,
  deleteAccount,
  type SellerDepositAccount,
} from '@/supabase/deposit/repository/depositAccountRepository';
import { listAccountVerifications } from '@/supabase/deposit/repository/onboardingRepository';
import type { VerificationStatus } from '@/supabase/deposit/types/onboarding';

// ==================== 타입 ====================

interface DepositAccountManagerProps {
  onChanged?: () => void;
}

// ==================== 계좌번호 마스킹 ====================

function maskAccountNumber(accountNumber: string): string {
  if (accountNumber.length <= 4) return accountNumber;
  return `****${accountNumber.slice(-4)}`;
}

// ==================== 지원 은행 ====================
// 파이프라인 파서(supabase/functions/sms-deposit/parser.ts)가 실제 감지/파싱하는 은행만.
// bank_name엔 canonical 코드 저장(parsed_deposit.bank_name과 동일 값), 화면엔 한글 라벨 표시.
const SUPPORTED_BANKS = [
  { code: 'KB', label: 'KB국민' },
  { code: 'Shinhan', label: '신한' },
  { code: 'Woori', label: '우리' },
  { code: 'Hana', label: '하나' },
  { code: 'Nonghyup', label: 'NH농협' },
  { code: 'IBK', label: 'IBK기업' },
] as const;

function bankLabel(code: string | null): string {
  if (!code) return '은행 미지정';
  return SUPPORTED_BANKS.find((b) => b.code === code)?.label ?? code;
}

function verificationBadge(
  status: VerificationStatus | undefined,
): { label: string; cls: string; Icon: typeof BadgeCheck } | null {
  switch (status) {
    case 'confirmed':   return { label: '확인됨', cls: 'bg-teal-50 text-teal-700 ring-1 ring-teal-100', Icon: BadgeCheck };
    case 'provisional': return { label: '확인 대기', cls: 'bg-sky-50 text-sky-700 ring-1 ring-sky-100', Icon: Clock };
    case 'pending':     return { label: '미확인', cls: 'bg-slate-100 text-slate-500 ring-1 ring-slate-200/70', Icon: CircleDashed };
    default:            return null; // legacy/없음 = 배지 없음(기존 수동 계좌)
  }
}

// ==================== 메인 컴포넌트 ====================

export default function DepositAccountManager({ onChanged }: DepositAccountManagerProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        className="min-h-11"
        onClick={() => setOpen(true)}
      >
        <CreditCard className="h-4 w-4 mr-2" />
        연동 계좌 관리
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="sm:max-w-md max-h-[85vh] overflow-y-auto"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            document.body.style.pointerEvents = '';
          }}
        >
          <DialogHeader>
            <DialogTitle>연동 계좌 관리</DialogTitle>
            <DialogDescription className="sr-only">
              입금 매칭에 사용할 연동 계좌를 관리합니다.
            </DialogDescription>
          </DialogHeader>

          <DialogBody open={open} onChanged={onChanged} />
        </DialogContent>
      </Dialog>
    </>
  );
}

// ==================== 다이얼로그 내부 (상태 분리) ====================

function DialogBody({
  open,
  onChanged,
}: {
  open: boolean;
  onChanged?: () => void;
}) {
  const [accounts, setAccounts] = useState<SellerDepositAccount[]>([]);
  const [verifications, setVerifications] = useState<Record<number, VerificationStatus>>({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // 토글 중인 id 추적
  const [togglingId, setTogglingId] = useState<number | null>(null);

  // 삭제 확인 대상 + 삭제 진행 중 id
  const [confirmTarget, setConfirmTarget] = useState<SellerDepositAccount | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await listAccounts();
      setAccounts(data);
      try {
        setVerifications(await listAccountVerifications());
      } catch {
        setVerifications({}); // 배지만 생략, 목록은 유지
      }
    } catch {
      setLoadError('계좌 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadAccounts();
      setConfirmTarget(null);
    }
  }, [open, loadAccounts]);

  // ==================== 토글 ====================

  const handleToggle = useCallback(async (account: SellerDepositAccount) => {
    if (togglingId !== null) return;
    setTogglingId(account.id);
    const nextIsActive = !account.is_active;
    try {
      await setActive(account.id, nextIsActive);
      setAccounts((current) => current.map((item) => (
        item.id === account.id ? { ...item, is_active: nextIsActive } : item
      )));
      onChanged?.();
      toast.success(
        account.is_active ? '계좌를 중지했습니다.' : '계좌를 활성화했습니다.'
      );
    } catch {
      toast.error('상태 변경에 실패했습니다.');
    } finally {
      setTogglingId(null);
    }
  }, [togglingId, onChanged]);

  // ==================== 삭제 ====================

  const handleDelete = useCallback(async (account: SellerDepositAccount) => {
    if (deletingId !== null) return;
    setDeletingId(account.id);
    try {
      await deleteAccount(account.id);
      setConfirmTarget(null);
      await loadAccounts();
      onChanged?.();
      toast.success('계좌를 삭제했습니다.');
    } catch {
      toast.error('계좌 삭제에 실패했습니다.');
    } finally {
      setDeletingId(null);
    }
  }, [deletingId, loadAccounts, onChanged]);

  // ==================== 렌더링 ====================

  return (
    <div className="space-y-5">
      {/* 계좌 목록 */}
      <section>
        <h3 className="text-sm font-medium text-gray-700 mb-2">등록된 계좌</h3>

        {loading && (
          <div className="flex items-center justify-center py-6 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            <span className="text-sm">불러오는 중...</span>
          </div>
        )}

        {!loading && loadError && (
          <p className="text-sm text-red-500 py-3">{loadError}</p>
        )}

        {!loading && !loadError && accounts.length === 0 && (
          <p className="text-sm text-gray-400 py-3 text-center">
            등록된 계좌가 없습니다.
          </p>
        )}

        {!loading && !loadError && accounts.length > 0 && (
          <ul className="space-y-2">
            {accounts.map((account) => (
              <li
                key={account.id}
                className={cn(
                  'flex items-center justify-between gap-3 rounded-xl px-4 py-3.5 ring-1 transition-colors',
                  account.is_active
                    ? 'bg-white ring-slate-200'
                    : 'bg-slate-50/60 ring-slate-100'
                )}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className={cn('shrink-0 text-sm font-semibold', account.is_active ? 'text-slate-900' : 'text-slate-500')}>
                    {bankLabel(account.bank_name)}
                  </span>
                  <span className={cn('truncate font-mono text-sm tabular-nums', account.is_active ? 'text-slate-500' : 'text-slate-400')}>
                    {maskAccountNumber(account.account_number)}
                  </span>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {(() => {
                    const vb = verificationBadge(verifications[account.id]);
                    if (!vb) return null;
                    const Icon = vb.Icon;
                    return (
                      <Badge variant="secondary" className={cn('gap-1 border-0 text-xs font-medium', vb.cls)}>
                        <Icon className="h-3 w-3" />{vb.label}
                      </Badge>
                    );
                  })()}
                  <Badge
                    variant="secondary"
                    className={cn(
                      'border-0 text-xs font-medium',
                      account.is_active
                        ? 'bg-teal-50 text-teal-700 ring-1 ring-teal-100'
                        : 'bg-slate-100 text-slate-500 ring-1 ring-slate-200/70'
                    )}
                  >
                    {account.is_active ? '사용중' : '중지됨'}
                  </Badge>

                  <Button
                    variant="outline"
                    size="sm"
                    aria-label={account.is_active ? '중지' : '활성화'}
                    aria-busy={togglingId === account.id}
                    aria-disabled={togglingId === account.id}
                    className="min-h-8 w-16 shrink-0 rounded-lg border-slate-200 px-0 text-xs font-medium text-slate-600 hover:bg-slate-50 aria-disabled:cursor-wait aria-disabled:opacity-60"
                    onClick={() => handleToggle(account)}
                  >
                    {togglingId === account.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : account.is_active ? (
                      '중지'
                    ) : (
                      '활성화'
                    )}
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    aria-label="계좌 삭제"
                    className="min-h-8 rounded-lg border-slate-200 px-2 text-slate-400 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                    disabled={deletingId === account.id}
                    onClick={() => setConfirmTarget(account)}
                  >
                    {deletingId === account.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 삭제 확인 */}
      <Dialog
        open={confirmTarget !== null}
        onOpenChange={(o) => {
          if (!o && deletingId === null) setConfirmTarget(null);
        }}
      >
        <DialogContent
          className="sm:max-w-sm"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            document.body.style.pointerEvents = '';
          }}
        >
          <DialogHeader>
            <DialogTitle>계좌를 삭제할까요?</DialogTitle>
            <DialogDescription>
              {confirmTarget && (
                <>
                  <span className="font-medium text-slate-700">
                    {bankLabel(confirmTarget.bank_name)} {maskAccountNumber(confirmTarget.account_number)}
                  </span>
                  {' '}계좌를 삭제합니다. 지난 입금·매칭 이력은 그대로 보존되며 계좌 연결만 해제됩니다. 앞으로 이 계좌로 들어오는 입금은 자동 매칭되지 않아요. 이 작업은 되돌릴 수 없습니다.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              className="min-h-10"
              disabled={deletingId !== null}
              onClick={() => setConfirmTarget(null)}
            >
              취소
            </Button>
            <Button
              className="min-h-10 bg-red-600 hover:bg-red-700 text-white"
              disabled={deletingId !== null}
              onClick={() => confirmTarget && handleDelete(confirmTarget)}
            >
              {deletingId !== null ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              삭제
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
